import * as React from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import styled from 'styled-components'
import { useClickAway } from 'ahooks'
import { cloneElement } from '../_util/reactNode'
import { isWindow } from '../_util/is'
import { fadeConfig } from '../../configs/motion'

const PortalWrapper = styled(motion.div)<{ $zIndex: number }>`
  position: fixed;
  /* position: absolute; */
  z-index: ${({ $zIndex }) => $zIndex};
`

export type Placement =
  | 'top'
  | 'topLeft'
  | 'topRight'
  | 'right'
  | 'rightTop'
  | 'rightBottom'
  | 'bottom'
  | 'bottomLeft'
  | 'bottomRight'
  | 'left'
  | 'leftTop'
  | 'leftBottom'

export interface TargetProps {
  placement?: Placement
  action?: 'hover' | 'click'
  zIndex?: number
  safeArea?: React.ReactNode
  trigger?: React.ReactNode
  triggerClick?: boolean
  children?: React.ReactNode
}

interface PortalProps extends TargetProps {
  setTargetRef: React.RefObject<HTMLDivElement>
  setPopupRef: React.RefObject<HTMLDivElement>
  handleInsideClick: (...args: any[]) => any
}

// const useOutsideClick = (ref: React.RefObject<HTMLDivElement>, handler: () => void) => {
//   React.useEffect(() => {
//     const listener = (event: MouseEvent) => {
//       // 确保点击事件不是在目标元素上发生的
//       if (!ref.current || ref.current.contains(event.target as Node)) {
//         return
//       }
//       // 使用 setTimeout 来避免立即设置状态，这样 onClick 事件处理的状态变化就不会被覆盖
//       // setTimeout(handler, 0);
//       handler()
//     }
//     document.addEventListener('mousedown', listener)
//     return () => {
//       document.removeEventListener('mousedown', listener)
//     }
//   }, [ref, handler])
// }

const calculateDirection = (targetRect: DOMRect, popupRect: DOMRect, placement: Placement): React.CSSProperties => {
  const { top: targetTop, left: targetLeft, width: targetWidth, height: targetHeight } = targetRect
  const { width: popupWidth, height: popupHeight } = popupRect

  const result: Record<Placement, any> = {
    top: { top: targetTop - popupHeight, left: targetLeft - (popupWidth - targetWidth) / 2 },
    topLeft: { top: targetTop - popupHeight, left: targetLeft },
    topRight: { top: targetTop - popupHeight, left: targetLeft + targetWidth - popupWidth },
    right: { top: targetTop + (targetHeight - popupHeight) / 2, left: targetLeft + targetWidth },
    rightTop: { top: targetTop, left: targetLeft + targetWidth },
    rightBottom: { top: targetTop + targetHeight - popupHeight, left: targetLeft + targetWidth },
    bottom: { top: targetTop + targetHeight, left: targetLeft - (popupWidth - targetWidth) / 2 },
    bottomLeft: { top: targetTop + targetHeight, left: targetLeft },
    bottomRight: { top: targetTop + targetHeight, left: targetLeft + targetWidth - popupWidth },
    left: { top: targetTop + (targetHeight - popupHeight) / 2, left: targetLeft - popupWidth },
    leftTop: { top: targetTop, left: targetLeft - popupWidth },
    leftBottom: { top: targetTop + targetHeight - popupHeight, left: targetLeft - popupWidth },
  }

  return result[placement]
}

const Portal: React.FC<PortalProps> = (props: PortalProps) => {
  const { setTargetRef, setPopupRef, placement = 'top', zIndex = 1_000, safeArea, trigger, handleInsideClick } = props

  const [direction, setDirection] = React.useState<React.CSSProperties>({})

  React.useEffect(() => {
    if (!setTargetRef.current || !setPopupRef.current) return
    const targetRect = setTargetRef.current.getBoundingClientRect()
    const popupRect = setPopupRef.current.getBoundingClientRect()
    const newDirection = calculateDirection(targetRect, popupRect, placement)
    setDirection(newDirection)
  }, [setTargetRef, setPopupRef, placement])

  return createPortal(
    <PortalWrapper
      ref={setPopupRef}
      $zIndex={zIndex}
      style={direction}
      onClick={(e) => {
        e.stopPropagation()
      }}
      {...fadeConfig}
    >
      {cloneElement(safeArea)}
      {cloneElement(trigger, { onClick: handleInsideClick })}
    </PortalWrapper>,
    window.document.body,
  )
}

const Trigger: React.FC<TargetProps> = (props: TargetProps) => {
  const { action = 'hover', children, triggerClick = true } = props

  const setTargetRef = React.useRef<HTMLDivElement>(null)
  const setPopupRef = React.useRef<HTMLDivElement>(null)

  const [open, setOpen] = React.useState<boolean>(false)

  const handleDocumentMouseLeave = React.useCallback(
    (e: MouseEvent) => {
      // 监听整个文档的鼠标移动事件以处理鼠标移出Popup的情况
      if (
        action === 'hover' &&
        open &&
        !setTargetRef.current?.contains(e.target as Node) &&
        !setPopupRef.current?.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    },
    [action, open],
  )

  const handleMouseEnter = React.useCallback(() => {
    if (action === 'hover') {
      setOpen(true)
    }
  }, [action])

  const handleMouseLeave = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // 当鼠标移出目标元素时，如果不是移入Popup元素，则关闭Popup
      const relatedTarget = e.relatedTarget as Node | Window
      if (isWindow(relatedTarget)) return
      if (action === 'hover' && !setPopupRef.current?.contains(relatedTarget)) {
        setOpen(false)
      }
    },
    [action],
  )

  React.useEffect(() => {
    if (action === 'click') return
    document.addEventListener('mousemove', handleDocumentMouseLeave)
    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('mousemove', handleDocumentMouseLeave)
    }
  }, [action, handleDocumentMouseLeave])

  // const handleToggle = () => {
  //   if (action === 'click') {
  //     setOpen((v) => !v)
  //   }
  // }

  // handle event
  const handleOpen: React.MouseEventHandler<HTMLDivElement> = () => {
    if (action !== 'click') return
    setOpen(true)
  }

  const handleClose: React.MouseEventHandler<HTMLDivElement> = () => {
    if (action !== 'click' || !triggerClick) return
    setOpen(false)
  }

  // const handleInsideClick = React.useCallback(() => {
  //   if (triggerClick) {
  //     setOpen(false)
  //   }
  // }, [triggerClick])

  // useOutsideClick(setPopupRef, () => {
  //   if (action === 'click') {
  //     setOpen(false)
  //   }
  // })

  useClickAway(() => {
    if (action === 'click') {
      setOpen(false)
    }
  }, setTargetRef)

  return (
    <>
      <div
        ref={setTargetRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        // onClick={handleToggle}
        onClick={handleOpen}
        aria-hidden
      >
        {cloneElement(children)}
      </div>
      <AnimatePresence>
        {open && (
          <Portal
            setTargetRef={setTargetRef}
            setPopupRef={setPopupRef}
            // handleInsideClick={handleInsideClick}
            handleInsideClick={handleClose}
            {...props}
          />
        )}
      </AnimatePresence>
    </>
  )
}

export default Trigger
